import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Carregando } from '@/components/comum/Carregando';
import type { PerfilUsuario } from '@/tipos';

/** Tela da troca obrigatória: a única rota autenticada liberada com a senha provisória. */
const ROTA_TROCAR_SENHA = '/trocar-senha';

interface RotaProtegidaProps {
  children: React.ReactNode;
  permissoes?: string[];
  perfis?: PerfilUsuario[];
}

export function RotaProtegida({ children, permissoes, perfis }: RotaProtegidaProps) {
  const { usuario, isAutenticado, isCarregando, temPermissao, temPerfil } = useAuth();
  const location = useLocation();

  if (isCarregando) {
    return <Carregando fullscreen texto="Verificando autenticação..." />;
  }

  if (!isAutenticado) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Conta com a senha provisória (primeiro acesso ou senha resetada pela
  // secretaria) não entra no sistema. O desvio existia só na tela de login:
  // digitando /painel ou /dashboard, a pessoa seguia com a provisória, que é a
  // data de nascimento. Toda rota autenticada passa por aqui, então a cancela
  // vale para todas; a própria troca de senha fica de fora, senão redirecionaria
  // para si mesma.
  if (usuario?.requer_troca_senha && location.pathname !== ROTA_TROCAR_SENHA) {
    return <Navigate to={ROTA_TROCAR_SENHA} replace />;
  }

  // Verifica perfis permitidos
  if (perfis && perfis.length > 0 && !temPerfil(...perfis)) {
    return <Navigate to="/sem-permissao" replace />;
  }

  // Verifica permissões
  if (permissoes && permissoes.length > 0) {
    const temAlgumaPermissao = permissoes.some(p => temPermissao(p));
    if (!temAlgumaPermissao) {
      return <Navigate to="/sem-permissao" replace />;
    }
  }

  return <>{children}</>;
}
